import cn from 'classnames';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DAY_ITEM_WIDTH = 68;
const GAP = 4;

interface DateStripCalendarProps {
  value: string; // YYYY-MM-DD
  onChange: (dateString: string) => void;
}

export const DateStripCalendar = ({ value, onChange }: DateStripCalendarProps) => {
  const selectedDate = dayjs(value);
  const today = dayjs();
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleDays, setVisibleDays] = useState(14);

  const [startDate, setStartDate] = useState<Dayjs>(() => dayjs(value).startOf('month'));

  useEffect(() => {
    const newDate = dayjs(value);
    if (newDate.isBefore(startDate) || newDate.isAfter(startDate.add(visibleDays - 1, 'day'))) {
      setStartDate(newDate.startOf('month'));
    }
  }, [value]);

  const calculateVisibleDays = useCallback(() => {
    if (!containerRef.current) return;
    const width = containerRef.current.offsetWidth;
    const count = Math.floor((width + GAP) / (DAY_ITEM_WIDTH + GAP));
    setVisibleDays(Math.max(count, 1));
  }, []);

  useEffect(() => {
    calculateVisibleDays();
    const observer = new ResizeObserver(calculateVisibleDays);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [calculateVisibleDays]);

  const days = useMemo(() => {
    const result: Dayjs[] = [];
    for (let i = 0; i < visibleDays; i++) {
      result.push(startDate.add(i, 'day'));
    }
    return result;
  }, [startDate, visibleDays]);

  const handlePrev = () => {
    setStartDate((prev) => prev.subtract(1, 'day'));
  };

  const handleNext = () => {
    setStartDate((prev) => prev.add(1, 'day'));
  };

  const handleSelect = (date: Dayjs) => {
    onChange(date.format('YYYY-MM-DD'));
  };

  return (
    <div className="d-flex align-items-center gap-2">
      <button type="button" className="btn btn-sm btn-icon btn-light-secondary rounded-circle" onClick={handlePrev}>
        <i className="bi bi-chevron-left fs-6" />
      </button>

      <div ref={containerRef} className="d-flex gap-1 overflow-hidden flex-grow-1 justify-content-between">
        {days.map((day) => {
          const isSelected = day.isSame(selectedDate, 'day');
          const isToday = day.isSame(today, 'day');

          return (
            <div
              key={day.format('YYYY-MM-DD')}
              className={cn(
                'd-flex flex-column align-items-center justify-content-center rounded-3 cursor-pointer px-3 py-1 min-w-65px lh-1',
                {
                  'bg-white': !isSelected,
                  'bg-secondary text-white': isSelected,
                  'bg-light-secondary': isToday && !isSelected
                }
              )}
              onClick={() => handleSelect(day)}
            >
              <span
                className={cn('fs-9 fw-medium text-uppercase lh-sm', {
                  'text-white': isSelected,
                  'text-gray-500': !isSelected
                })}
              >
                {day.format('MMM')}
              </span>
              <span
                className={cn('fs-2 fw-bolder lh-1', {
                  'text-white': isSelected,
                  'text-gray-800': !isSelected
                })}
              >
                {day.format('DD')}
              </span>
              <span
                className={cn('fs-9 fw-medium lh-sm', {
                  'text-white': isSelected,
                  'text-gray-500': !isSelected
                })}
              >
                {isToday ? 'Today' : day.format('dddd')}
              </span>
            </div>
          );
        })}
      </div>

      <button type="button" className="btn btn-sm btn-icon btn-light-secondary rounded-circle" onClick={handleNext}>
        <i className="bi bi-chevron-right fs-6" />
      </button>
    </div>
  );
};
